import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { ScheduleType } from '@octano/global-ui';
import { v4 as uuidv4 } from 'uuid';

import {
  generateLessonDates,
  getDayIdByDate,
} from '../../../SectionsMaintainer/utils/index';
import { useGetDays } from '../../../SectionsMaintainer/hooks/useGetDays';
import { AcademicOfferScheduleSection } from '../types';

/**
 * Hook para el calendario semanal agregado de todas las secciones de una oferta de EC.
 * Adapta useWeeklyScheduleCalendar para trabajar con múltiples secciones.
 *
 * @param sections - Array de secciones con sus sectionSchedules
 * @returns Estado del calendario, funciones de navegación y datos para renderizar
 */
export function useAcademicOfferWeeklyCalendar(
  sections: AcademicOfferScheduleSection[],
) {
  const { t } = useTranslation();

  // Estado para navegación semanal
  const [selectedWeek, setSelectedWeek] = useState({
    selectedDay: dayjs(),
    start: dayjs().startOf('w'),
    end: dayjs().endOf('w'),
  });

  // Headers de días con fechas específicas
  const days = useGetDays(selectedWeek);

  // Funciones de navegación
  const nextWeek = useCallback(() => {
    setSelectedWeek((prev) => ({
      selectedDay: prev.selectedDay.add(1, 'w'),
      start: prev.start.add(1, 'w'),
      end: prev.end.add(1, 'w'),
    }));
  }, []);

  const previousWeek = useCallback(() => {
    setSelectedWeek((prev) => ({
      selectedDay: prev.selectedDay.subtract(1, 'w'),
      start: prev.start.subtract(1, 'w'),
      end: prev.end.subtract(1, 'w'),
    }));
  }, []);

  const selectWeek = useCallback((date: string | Date | null) => {
    const selectedDate = dayjs(date || undefined);
    setSelectedWeek({
      selectedDay: selectedDate,
      start: selectedDate.startOf('w'),
      end: selectedDate.endOf('w'),
    });
  }, []);

  // Agregar todos los sectionSchedules de todas las secciones
  const allSectionSchedules = useMemo(() => {
    return sections.flatMap((section) =>
      section.sectionSchedules
        .filter((schedule) => !schedule.deletedAt)
        .map((schedule) => ({ ...schedule, _section: section })),
    );
  }, [sections]);

  // Calcular todas las fechas que tienen clases (para DateInput red dots)
  const datesWithEvents = useMemo(() => {
    const allDates: Date[] = [];
    const yearStart = selectedWeek.selectedDay
      .startOf('year')
      .subtract(1, 'year');
    const yearEnd = selectedWeek.selectedDay.endOf('year').add(1, 'year');

    allSectionSchedules.forEach((schedule) => {
      const dates = generateLessonDates(schedule);
      const datesInYear = dates.filter(
        (date) => !(date.isBefore(yearStart) || date.isAfter(yearEnd)),
      );
      datesInYear.forEach((date) => {
        allDates.push(date.toDate());
      });
    });

    return allDates;
  }, [allSectionSchedules, selectedWeek.selectedDay]);

  // Generar instancias de clases para la semana seleccionada (multi-sección)
  const schedules: ScheduleType[] = useMemo(() => {
    const lessonInstances: ScheduleType[] = [];

    allSectionSchedules.forEach((scheduleWithSection) => {
      const { _section: section, ...schedule } = scheduleWithSection;
      const dates = generateLessonDates(schedule);

      const datesInWeek = dates.filter(
        (date) =>
          !(
            date.isBefore(selectedWeek.start) || date.isAfter(selectedWeek.end)
          ),
      );

      datesInWeek.forEach((date) => {
        lessonInstances.push({
          sectionId: section.id,
          scheduleId: uuidv4() as unknown,
          courseCode: section.course.name,
          sectionName: section.name,
          day: getDayIdByDate(date),
          startDate: date.format('YYYY-MM-DD'),
          endDate: date.format('YYYY-MM-DD'),
          activityId: schedule.activityId,
          moduleStartId: schedule.startModule.id,
          moduleEndId: schedule.endModule?.id ?? 0,
          modality: schedule.modality?.deliveryMode,
          tooltip: {
            courseName: section.course.name,
            sectionTitle: t('common.scheduleTooltip.sectionTitle'),
            sectionName: section.name,
            teachers: [schedule.responsibles[0]?.account?.fullName ?? ''],
            teachersTitle: t('common.scheduleTooltip.teacherTitle'),
            classroom: schedule.classroom?.name ?? '',
            modality: schedule.modality?.deliveryMode,
          },
        } as ScheduleType);
      });
    });

    return lessonInstances;
  }, [allSectionSchedules, selectedWeek, t]);

  return {
    selectedWeek,
    nextWeek,
    previousWeek,
    selectWeek,
    schedules,
    datesWithEvents,
    days,
    hasSchedules: allSectionSchedules.length > 0,
  };
}
